import { forwardRef, useEffect, useId, useImperativeHandle, useRef, useState, type CSSProperties, type HTMLAttributes, type ReactNode } from "react";
// `use` aliased: ESLint's react-hooks rule pattern-matches any `use*`
// identifier as a hook call regardless of where it comes from, and
// echarts' own module-registration function is genuinely named `use`.
import { init, use as echartsUse, type EChartsType } from "echarts/core";
import { LineChart as EChartsLineChart, BarChart as EChartsBarChart, PieChart as EChartsPieChart } from "echarts/charts";
import { GridComponent, TooltipComponent, LegendComponent, TitleComponent, MarkLineComponent } from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import { usePrefersReducedMotion } from "./hooks";
import { useThemeMode } from "@/lib/theme";
import { cn } from "@/lib/cn";

// Stage 6: local replacement for @datasirpi/ui's chart family (Chart,
// AreaChart, BarChart, PieChart, Sparkline, useChartTheme). The vendor's
// own components are thin wrappers around Apache ECharts — `echarts` is
// already a direct dependency of this app (confirmed in the de-vendor
// audit), so this ports the row-data → ECharts-option translation logic
// letter-for-letter from the vendor's compiled bundle, not a from-scratch
// charting engine. `ScatterChart`/`LineChart` (the un-aliased direct
// export) are NOT ported — zero real usage anywhere in this app.
//
// Registration is scoped to exactly what this app's real usage needs
// (Line/Bar/Pie + Grid/Tooltip/Legend/Title/MarkLine + Canvas renderer) —
// matches the vendor's own per-chart-type `registerXChart()` functions,
// just flattened into one registration since every chart type this app
// actually uses is always potentially on the page.
let registered = false;
function registerCharts() {
  if (registered) return;
  registered = true;
  echartsUse([
    CanvasRenderer,
    EChartsLineChart,
    EChartsBarChart,
    EChartsPieChart,
    GridComponent,
    TooltipComponent,
    LegendComponent,
    TitleComponent,
    MarkLineComponent,
  ]);
}
registerCharts();

export interface ChartTheme {
  mode: "light" | "dark";
  /** Categorical series colours. Carry no severity or brand meaning by design. */
  palette: string[];
  /** Severity ramp in rank order: critical, high, medium, low, info. A chart
   *  ABOUT severity reads this, so it agrees with every badge and chip. */
  severity: string[];
  textColor: string;
  textMuted: string;
  axisLine: string;
  gridLine: string;
  tooltipBg: string;
  tooltipBorder: string;
  fontFamily: string;
  bg: string;
}

const FALLBACK: ChartTheme = {
  mode: "light",
  // Phase 2.4 — the same tokens buildTheme() reads at runtime, so an SSR or
  // pre-hydration render still gets this system's palette. Categorical only:
  // no brand, and nothing that reads as good or bad.
  // d2d-DetectHub eval 2.3: kept in step with config.css's light --chart-1..6
  // and --hue-*. It still held the pre-eval #175cd3 (Info's old blue) and
  // violet #6941c6 in chart-1 / chart-3, so a pre-hydration render drew a
  // series in a status hue.
  palette: ["#0b7bc0", "#107569", "#696634", "#c11574", "#475467", "#363f72"],
  severity: ["#8a1c13", "#b4380f", "#8a5300", "#05603a", "#1849a9"],
  textColor: "#1a1a1a",
  textMuted: "#6b7280",
  axisLine: "#d4d4d8",
  gridLine: "#e5e7eb",
  tooltipBg: "#ffffff",
  tooltipBorder: "#d4d4d8",
  fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, Ubuntu, Cantarell, "Noto Sans", sans-serif',
  bg: "#ffffff",
};

function readVar(name: string, root: HTMLElement, fallback: string): string {
  const v = getComputedStyle(root).getPropertyValue(name).trim();
  return v || fallback;
}

// Resolve a caller-supplied colour that may be a design token reference.
//
// This exists because of a real, silent failure mode, not as a convenience.
// These charts render through ECharts' CanvasRenderer, and canvas ignores an
// unparseable fillStyle ENTIRELY: assigning `ctx.fillStyle = "var(--x)"`
// leaves the PREVIOUS fill in place rather than throwing or falling back
// (verified in Chrome, not assumed). So a call site that passed a token — as
// the dashboard's severity ramp did for every pie slice — got slices painted
// in whatever colour ECharts happened to use last, with nothing in the
// console to say so. Tokens are the correct thing for a call site to pass,
// so they are resolved to a real value here, once, before reaching an option.
//
// Called from inside each chart's `option` useMemo, which lists `theme` as a
// dependency, so a theme flip re-resolves rather than keeping stale colours.
// Same colour at a given alpha, for gradient stops and translucent fills.
// Needed because these render to canvas: `color-mix()` and 8-digit hex with a
// CSS variable are not available there, so the alpha has to be baked into a
// literal rgba() before it reaches an ECharts option. Accepts what
// resolveChartColor() returns — #rgb, #rrggbb, rgb()/rgba() — and falls back
// to the input untouched for anything it does not recognise (named colours,
// for instance), which degrades to a solid fill rather than a broken one.
export function withAlpha(color: string, alpha: number): string {
  const c = color.trim();
  let m = /^#([0-9a-f]{3})$/i.exec(c);
  if (m) {
    const [r, g, b] = m[1].split("").map((h) => parseInt(h + h, 16));
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  m = /^#([0-9a-f]{6})$/i.exec(c);
  if (m) {
    const n = parseInt(m[1], 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
  }
  m = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i.exec(c);
  if (m) return `rgba(${m[1]}, ${m[2]}, ${m[3]}, ${alpha})`;
  return c;
}

const SEVERITY_ORDER = ["critical", "high", "medium", "low", "info"] as const;

/**
 * Colour for a severity series, so a severity chart matches the badges beside
 * it. Anything that is not one of the five ranks falls back to a categorical
 * slot rather than guessing at a severity.
 */
export function severityChartColor(theme: ChartTheme, level: string | null | undefined, fallbackIndex = 0): string {
  const i = SEVERITY_ORDER.indexOf((level ?? "").trim().toLowerCase() as (typeof SEVERITY_ORDER)[number]);
  return i >= 0 ? theme.severity[i] : theme.palette[fallbackIndex % theme.palette.length];
}

export function resolveChartColor(value: string | undefined, fallback: string): string {
  if (!value) return fallback;
  let v = value.trim();
  if (typeof document === "undefined") return v.startsWith("var(") ? fallback : v;
  const root = document.documentElement;
  // Depth-capped: a token may alias another (--d2d-accent -> --brand).
  // getComputedStyle normally substitutes those already; the loop is here so
  // a value that arrives un-substituted still terminates instead of looping.
  for (let depth = 0; depth < 4 && v.startsWith("var("); depth++) {
    const inner = v.slice(4, v.lastIndexOf(")"));
    const comma = inner.indexOf(",");
    const name = (comma === -1 ? inner : inner.slice(0, comma)).trim();
    const inlineFallback = comma === -1 ? "" : inner.slice(comma + 1).trim();
    v = readVar(name, root, inlineFallback).trim();
  }
  return !v || v.startsWith("var(") ? fallback : v;
}

function buildTheme(mode: "light" | "dark"): ChartTheme {
  if (typeof document === "undefined") return { ...FALLBACK, mode };
  const root = document.documentElement;
  // Phase 2.4 — was [brand-600, success, warning, info, brand-400, danger]:
  // two near-identical oranges in one categorical set, plus green/red telling
  // the reader an arbitrary category was good or bad.
  const palette = [
    readVar("--chart-1", root, FALLBACK.palette[0]),
    readVar("--chart-2", root, FALLBACK.palette[1]),
    readVar("--chart-3", root, FALLBACK.palette[2]),
    readVar("--chart-4", root, FALLBACK.palette[3]),
    readVar("--chart-5", root, FALLBACK.palette[4]),
    readVar("--chart-6", root, FALLBACK.palette[5]),
  ];
  const severity = [
    readVar("--hue-critical", root, FALLBACK.severity[0]),
    readVar("--hue-high", root, FALLBACK.severity[1]),
    readVar("--hue-medium", root, FALLBACK.severity[2]),
    readVar("--hue-low", root, FALLBACK.severity[3]),
    readVar("--hue-info", root, FALLBACK.severity[4]),
  ];
  return {
    mode,
    palette,
    severity,
    textColor: readVar("--ds-color-text-default", root, FALLBACK.textColor),
    textMuted: readVar("--ds-color-text-muted", root, FALLBACK.textMuted),
    axisLine: readVar("--ds-color-border-default", root, FALLBACK.axisLine),
    gridLine: readVar("--ds-color-border-default", root, FALLBACK.gridLine),
    tooltipBg: readVar("--ds-color-bg-default", root, FALLBACK.tooltipBg),
    tooltipBorder: readVar("--ds-color-border-default", root, FALLBACK.tooltipBorder),
    fontFamily: readVar("--ds-font-family-sans", root, FALLBACK.fontFamily),
    bg: readVar("--ds-color-bg-default", root, FALLBACK.bg),
  };
}

function sameTheme(a: ChartTheme, b: ChartTheme): boolean {
  return (
    a.mode === b.mode &&
    a.textColor === b.textColor &&
    a.textMuted === b.textMuted &&
    a.axisLine === b.axisLine &&
    a.gridLine === b.gridLine &&
    a.tooltipBg === b.tooltipBg &&
    a.tooltipBorder === b.tooltipBorder &&
    a.bg === b.bg &&
    a.fontFamily === b.fontFamily &&
    a.palette.join() === b.palette.join() &&
    a.severity.join() === b.severity.join()
  );
}

// Charts render to <canvas>, so CSS variables can't reach axis/legend/label
// text — colors are resolved in JS and passed into the ECharts option. The
// local ThemeProvider writes `data-ds-mode` to <html> in an effect that
// runs AFTER this hook re-renders on a mode change, so reading the
// variables synchronously during render would capture the *previous*
// mode. Re-read whenever the applied `data-ds-mode` attribute actually
// changes instead, via a MutationObserver — independent of React effect
// ordering. `sameTheme` avoids a redundant setOption (and the entry-
// animation cancel it causes) when the resolved values are unchanged.
export function useChartTheme(): ChartTheme {
  // resolvedMode's type allows "hc", but nothing in this app ever reaches
  // it (theme.tsx only ever sets "light"/"dark") — narrowed defensively,
  // same pattern Sidebar.tsx already uses for AppSidebar's `theme` prop.
  const { resolvedMode } = useThemeMode();
  const mode: "light" | "dark" = resolvedMode === "light" ? "light" : "dark";
  const [theme, setTheme] = useState(() => buildTheme(mode));
  useEffect(() => {
    const read = () =>
      setTheme((prev) => {
        const next = buildTheme(mode);
        return sameTheme(prev, next) ? prev : next;
      });
    read();
    if (typeof MutationObserver === "undefined") return;
    const obs = new MutationObserver(read);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["data-ds-mode"] });
    return () => obs.disconnect();
  }, [mode]);
  return theme;
}

export function commonStyles(theme: ChartTheme, opts: { reducedMotion?: boolean } = {}) {
  const animate = !opts.reducedMotion;
  return {
    color: theme.palette,
    backgroundColor: "transparent",
    textStyle: { fontFamily: theme.fontFamily, color: theme.textColor },
    animation: animate,
    // Stage 6, motion gap: was 800ms — 2.5-8x over design.md's Standard
    // band (180-250ms). 250ms is the top of that band.
    animationDuration: animate ? 250 : 0,
    animationDurationUpdate: animate ? 300 : 0,
    animationEasing: "cubicOut",
    animationEasingUpdate: "cubicOut",
  };
}

export function commonTooltip(theme: ChartTheme) {
  return {
    confine: true,
    backgroundColor: theme.tooltipBg,
    borderColor: theme.tooltipBorder,
    borderWidth: 1,
    textStyle: { color: theme.textColor, fontFamily: theme.fontFamily },
    extraCssText: "box-shadow: 0 4px 12px rgba(0,0,0,0.08); z-index: 99;",
  };
}

export function commonAxis(theme: ChartTheme) {
  return {
    axisLine: { lineStyle: { color: theme.axisLine } },
    axisTick: { lineStyle: { color: theme.axisLine } },
    axisLabel: { color: theme.textMuted, fontFamily: theme.fontFamily },
    splitLine: { lineStyle: { color: theme.gridLine, type: "dashed" } },
  };
}

export function commonLegend(theme: ChartTheme) {
  return {
    bottom: 0,
    icon: "circle",
    itemHeight: 8,
    itemWidth: 8,
    itemGap: 16,
    textStyle: { color: theme.textColor, fontFamily: theme.fontFamily },
  };
}

function px(v: number | string): string {
  return typeof v === "number" ? `${v}px` : v;
}

export type EChartsOptionObj = Record<string, unknown>;

export interface ChartTabularData {
  caption?: string;
  headers: ReactNode[];
  rows: ReactNode[][];
}

export interface ChartHandle {
  getInstance: () => EChartsType | null;
}

export interface ChartProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  option: EChartsOptionObj;
  height?: number | string;
  width?: number | string;
  loading?: boolean;
  empty?: ReactNode;
  notMerge?: boolean;
  onEvents?: Record<string, (params: unknown) => void>;
  "aria-label": string;
  description?: ReactNode;
  tabularData?: ChartTabularData;
}

function isEmptyOption(option: EChartsOptionObj): boolean {
  const series = option.series as unknown;
  if (!series) return true;
  const list = Array.isArray(series) ? series : [series];
  if (list.length === 0) return true;
  return list.every((s) => {
    const data = (s as { data?: unknown[] }).data;
    return !Array.isArray(data) || data.length === 0;
  });
}

export const Chart = forwardRef<ChartHandle, ChartProps>(function Chart(
  { option, height = 240, width = "100%", loading = false, empty, notMerge = false, onEvents, className, "aria-label": ariaLabel, description, tabularData, ...rest },
  ref
) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const instanceRef = useRef<EChartsType | null>(null);
  const [ready, setReady] = useState(0);
  const baseId = useId();
  const descId = description ? `${baseId}-desc` : undefined;
  const tableId = tabularData ? `${baseId}-table` : undefined;

  useImperativeHandle(ref, () => ({ getInstance: () => instanceRef.current }), []);

  const showEmpty = empty !== undefined && empty !== null && isEmptyOption(option);

  useEffect(() => {
    if (showEmpty) return;
    if (!containerRef.current) return;
    let resizeObserver: ResizeObserver | null = null;
    const inst = init(containerRef.current);
    instanceRef.current = inst;
    inst.setOption(option, { notMerge });
    if (typeof ResizeObserver !== "undefined") {
      let lastW = containerRef.current.clientWidth;
      let lastH = containerRef.current.clientHeight;
      resizeObserver = new ResizeObserver((entries) => {
        const e = entries[0];
        if (!e) return;
        const cr = e.contentRect;
        if (cr.width === lastW && cr.height === lastH) return;
        lastW = cr.width;
        lastH = cr.height;
        inst.resize();
      });
      resizeObserver.observe(containerRef.current);
    }
    setReady((t) => t + 1);
    return () => {
      resizeObserver?.disconnect();
      instanceRef.current?.dispose();
      instanceRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEmpty]);

  const initialOption = useRef(option);
  useEffect(() => {
    if (showEmpty) return;
    if (initialOption.current === option) return;
    instanceRef.current?.setOption(option, { notMerge });
  }, [option, notMerge, showEmpty]);

  useEffect(() => {
    const inst = instanceRef.current;
    if (!inst || !onEvents) return;
    const entries = Object.entries(onEvents);
    entries.forEach(([evt, handler]) => inst.on(evt, handler));
    return () => {
      entries.forEach(([evt, handler]) => inst.off(evt, handler));
    };
  }, [onEvents, ready]);

  return (
    <div className={cn("rc-chart", className)} style={{ width: px(width), height: px(height) } as CSSProperties} {...rest}>
      <div
        role="img"
        aria-label={ariaLabel}
        aria-describedby={[descId, tableId].filter(Boolean).join(" ") || undefined}
        aria-busy={loading || undefined}
        className="rc-chart__canvas"
        ref={containerRef}
      />
      {description ? (
        <span id={descId} className="rc-chart__sr-table">
          {description}
        </span>
      ) : null}
      {tabularData ? (
        <table id={tableId} className="rc-chart__sr-table" aria-label={ariaLabel}>
          {tabularData.caption ? <caption>{tabularData.caption}</caption> : null}
          <thead>
            <tr>
              {tabularData.headers.map((h, i) => (
                <th scope="col" key={i}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tabularData.rows.map((row, ri) => (
              <tr key={ri}>
                {row.map((cell, ci) => (
                  <td key={ci}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      {showEmpty ? <div className="rc-chart__empty">{empty}</div> : null}
      {loading ? (
        <div className="rc-chart__loading" role="status" aria-label="Loading chart">
          Loading…
        </div>
      ) : null}
    </div>
  );
});

export { usePrefersReducedMotion };
